import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function BetaCTA() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [state, setState] = useState("idle"); // idle | busy | done | error
    const [error, setError] = useState("");
    const [invite, setInvite] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        const cleanName = name.trim();
        const clean = email.trim();
        if (!cleanName) {
            setError("Please enter your name.");
            setState("error");
            return;
        }
        if (!EMAIL_RE.test(clean)) {
            setError("That email doesn't look right — try again.");
            setState("error");
            return;
        }
        setState("busy");
        setError("");
        try {
            const res = await fetch(`${API}/beta-signup`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: cleanName, email: clean, message: message.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Something went wrong");
            setInvite(data.invite_link || "");
            setState("done");
        } catch (err) {
            setError(err.message || "Something went wrong — try again.");
            setState("error");
        }
    };

    return (
        <section
            id="beta"
            className="relative max-sm:mt-0 max-sm:pt-4 px-4 py-10 sm:px-8 sm:py-28 lg:px-12"
            data-testid="beta-section"
        >
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[50vh] w-[70vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(3,135,226,0.07),transparent_65%)] blur-2xl" />
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="relative mx-auto max-w-2xl overflow-hidden rounded-[2rem] border border-neon/20 bg-gradient-to-b from-elevated/90 to-coal p-8 text-center sm:p-12"
                data-testid="beta-card"
            >
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.35em] text-neon sm:mb-4">
                    Private beta
                </p>
                <h3 className="font-display text-3xl font-black tracking-tight text-ivory sm:text-4xl">
                    First in,{" "}
                    <span className="font-['Playfair_Display',serif] font-extrabold italic text-neon">
                        first streamed
                    </span>
                    .
                </h3>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-dim sm:mt-4">
                    Leave your email and we'll send you an invite to the
                    RIVVR beta. No spam, no noise — just the link.
                </p>

                <AnimatePresence mode="wait">
                    {state === "done" ? (
                        <motion.div
                            key="done"
                            initial={{ opacity: 0, scale: 0.92 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{
                                duration: 0.5,
                                ease: [0.34, 1.56, 0.64, 1],
                            }}
                            className="mt-8"
                            data-testid="beta-success-card"
                        >
                            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-neon text-void">
                                <Check size={26} strokeWidth={3} />
                            </div>
                            <p className="font-display text-xl font-bold text-ivory">
                                You're on the river.
                            </p>
                            <p className="mt-2 text-sm text-dim">
                                We'll send your invite to{" "}
                                <span className="text-ivory">{email}</span>.
                            </p>
                            {invite ? (
                                <a
                                    data-testid="beta-invite-link"
                                    href={invite}
                                    className="mt-6 inline-block rounded-[14px] border border-neon/50 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-neon transition hover:bg-neon hover:text-void"
                                >
                                    Open your beta invite
                                </a>
                            ) : null}
                        </motion.div>
                    ) : (
                        <motion.form
                            key="form"
                            onSubmit={submit}
                            exit={{ opacity: 0, y: -12 }}
                            className="mt-6 sm:mt-8"
                            data-testid="beta-form"
                        >
                            <div className="flex flex-col gap-3">
                                <input
                                    data-testid="beta-name-input"
                                    type="text"
                                    autoComplete="name"
                                    placeholder="Your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full rounded-[14px] border border-white/10 bg-void/80 px-6 py-4 text-sm text-ivory outline-none transition placeholder:text-ghost focus:border-neon/60 focus:ring-2 focus:ring-neon/20"
                                />
                                <div className="flex flex-col gap-3 sm:flex-row">
                                    <input
                                        data-testid="beta-email-input"
                                        type="text"
                                        inputMode="email"
                                        autoComplete="email"
                                        placeholder="you@nightstream.io"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="flex-1 rounded-[14px] border border-white/10 bg-void/80 px-6 py-4 text-sm text-ivory outline-none transition placeholder:text-ghost focus:border-neon/60 focus:ring-2 focus:ring-neon/20"
                                    />
                                    <button
                                        data-testid="beta-submit-btn"
                                        disabled={state === "busy"}
                                        className="flex items-center justify-center gap-2 rounded-[14px] bg-neon px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-void transition hover:bg-ivory disabled:opacity-60"
                                    >
                                        {state === "busy" ? (
                                            <Loader2
                                                size={16}
                                                className="animate-spin"
                                            />
                                        ) : null}
                                        {state === "busy"
                                            ? "Sending"
                                            : "Get my invite"}
                                        {state !== "busy" ? (
                                            <ArrowRight size={14} />
                                        ) : null}
                                    </button>
                                </div>
                                <textarea
                                    data-testid="beta-message-input"
                                    rows={3}
                                    placeholder="Add a message (optional)"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    maxLength={1000}
                                    className="w-full resize-none rounded-[14px] border border-white/10 bg-void/80 px-6 py-4 text-sm text-ivory outline-none transition placeholder:text-ghost focus:border-neon/60 focus:ring-2 focus:ring-neon/20"
                                />
                            </div>
                            {state === "error" ? (
                                <p
                                    data-testid="beta-error"
                                    className="mt-3 text-xs text-surge"
                                >
                                    {error}
                                </p>
                            ) : null}
                            <p className="mt-5 text-[11px] uppercase tracking-[0.25em] text-ghost">
                                iOS first · via TestFlight
                            </p>
                        </motion.form>
                    )}
                </AnimatePresence>
            </motion.div>
        </section>
    );
}
